// Sentinel "no printer" value persisted to localStorage for the printer
// picker. `PRINTER_ID === 0` means "nothing chosen yet"; `NO_PRINT_ID` means
// the user explicitly chose no-print.
export const NO_PRINT_ID = 1;

// Firestore batched-write hard limit. Used by mass-mutation flows
// (reassignment, bulk delete, rotation moves) to chunk batches.
export const FIRESTORE_BATCH_LIMIT = 500;

// Caps shared by Yup schemas + the `inputProps.maxLength` defensive ceiling
// on each `<TextField>`. Picked so the printed label and roster cells stay
// readable; VBS-side validation should match.
export const EVENT_NAME_MAX_LENGTH = 100;
export const ATTENDEE_NAME_MAX_LENGTH = 100;
export const GROUP_NAME_MAX_LENGTH = 50;
export const TEACHERS_MAX_LENGTH = 100;
export const ROTATION_NAME_MAX_LENGTH = 50;
export const EMERGENCY_CONTACT_NAME_MAX_LENGTH = 100;
export const EMERGENCY_CONTACT_PHONE_MAX_LENGTH = 30;
export const EMERGENCY_CONTACT_RELATIONSHIP_MAX_LENGTH = 50;

// Ordered list of valid `grade_completed` / `Group.grade` values. The
// matching `Grade` type lives in `./types`.
export const GRADES = [
  'TK',
  'K',
  'K/1st',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
] as const;

// Verbose dropdown labels, mirroring `GRADE_LABELS` in the VBS registration
// app (`vbs-registration/shared/constants.ts`). VBS has no equivalent for
// 'TK' — kindergarten is its lowest grade — so we spell it out as
// "Transitional Kindergarten" here for visual consistency. Use these only
// for form labels; the stored Firestore value is still the short `Grade`
// string ('K', '1st', …) so VBS sync stays wire-compatible.
//
// 'K/1st' is a check-in-app-only value for combined classes (the school
// program's mixed K + 1st English class). VBS has no such grade and will
// never send it as `grade_completed`, so its absence from the VBS list is
// intentional — don't "sync" it away.
export const GRADE_LABELS: Record<(typeof GRADES)[number], string> = {
  TK: 'Transitional Kindergarten',
  K: 'Kindergarten',
  'K/1st': 'K / 1st Grade',
  '1st': '1st Grade',
  '2nd': '2nd Grade',
  '3rd': '3rd Grade',
  '4th': '4th Grade',
  '5th': '5th Grade',
  '6th': '6th Grade',
};

// Free-text admin note attached to an attendee's `group_preference` via
// the Roster pencil button. Picked to fit a reasonable paragraph; long
// enough for context, short enough that the tooltip and activity-log
// diff stay readable.
export const GROUP_PREFERENCE_NOTE_MAX_LENGTH = 500;
